import React, { lazy, Suspense, useRef, useState } from 'react'
import { Nav, TabContainer, TabContent, TabPane } from 'react-bootstrap'
import styled from 'styled-components'
import { themeColors } from '../helpers/themeColors'
import { ReactComponent as TrackingIcon } from '../assets/img/icons/tracking-tab-link.svg'
import { ReactComponent as ShipNowIcon } from '../assets/img/icons/ship-now.svg'
import { ReactComponent as CalculateRatesIcon } from '../assets/img/icons/calculate-rates.svg'
import { ReactComponent as PricingIcon } from '../assets/img/icons/pricing.svg'
import { ReactComponent as TrackingIconHover } from '../assets/img/icons/tracking-hover.svg'
import { ReactComponent as ShipNowIconHover } from '../assets/img/icons/ship-now-hover.svg'
import { ReactComponent as CalculateRatesIconHover } from '../assets/img/icons/calculate-rates-hover.svg'
import { ReactComponent as PricingIconHover } from '../assets/img/icons/pricing-hover.svg'
import { isIframed, joinClasses } from '../helpers/utils'
import { useAutoIframeHeight } from '../hooks/utility'
import { useHistory } from 'react-router-dom'
import { useSelector } from 'react-redux'

import { trackingViews } from '../constants/tracking'

const MARKETINGSITE = process.env.REACT_APP_MARKETING_SITE_URL
const colors = themeColors()

const CalculateRatesForm = lazy(() =>
  import('../components/hp-quick-access/CalculateRatesForm'),
)
const Pricing = lazy(() => import('../components/hp-quick-access/Pricing'))
const TrackingForm = lazy(() =>
  import('../components/hp-quick-access/TrackingForm'),
)

const TrackingResults = lazy(() =>
  import('../components/hp-quick-access/TrackingResults'),
)

const HPQuickAccessTabs = [
  {
    id: 'ship-now',
    text: 'Ship now',
    icon: ShipNowIcon,
    content: null,
    iconHover: ShipNowIconHover,
    hoverText: 'Quick and easy, try us!',
  },
  {
    id: 'calculate-rates',
    text: 'Calculate rates',
    icon: CalculateRatesIcon,
    iconHover: CalculateRatesIconHover,
    content: CalculateRatesForm,
    hoverText: 'Tell us where and when.',
  },
  {
    id: 'pricing',
    text: 'Pricing',
    icon: PricingIcon,
    content: Pricing,
    iconHover: PricingIconHover,
    hoverText: 'Get the best prices for your shipping.',
  },
  {
    id: 'tracking',
    text: 'Tracking',
    icon: TrackingIcon,
    iconHover: TrackingIconHover,
    content: TrackingForm,
    hoverText: 'Updates every step of the way',
  },
]

const QuickAccess = styled.div`
  width: 100%;
  @media screen and (max-width: 575px) {
    max-width: 400px;
  }
  @media screen and (min-width: 576px) and (max-width: 767px) {
    max-width: 550px;
  }
  @media screen and (min-width: 768px) {
    max-width: 800px;
  }
  .nav-tabs {
    border-bottom: 4px solid #4f4f4f;
    .nav-item {
      .nav-link {
        @media screen and (max-width: 575px) {
          padding: 0;
          width: 80px;
          height: 80px;
          h3.text-body-bigger2 {
            font-size: 14px;
            line-height: 18px;
            text-align: center;
          }
        }
        @media screen and (min-width: 576px) and (max-width: 767px) {
          width: 120px;
          height: 120px;
          h3.text-body-bigger2 {
            font-size: 1rem;
            line-height: 20px;
            text-align: center;
          }
        }
        @media screen and (min-width: 768px) {
          padding: 0;
          width: 184px;
          height: 176px;
        }
        transition: 0.25s;
        &.active {
          background: ${colors.darkBlue};
          h3 {
            font-weight: 600 !important;
          }
        }
      }
    }
  }
`

const Icon = styled.span`
  text-align: center;
  @media screen and (max-width: 767px) {
    .icon {
      height: auto;
      width: 30px;
    }
  }
  &.calculate-rates {
    margin-right: 8px;
  }
  &.tracking {
    margin-left: 11px;
  }
`

const MenuContent = styled.div`
  padding: 0.25rem;
  @media screen and (min-width: 768px) {
    height: 90px;
    padding: 0;
  }
`

function HomeQuickAccess() {
  const authenticated = useSelector(({ auth }) => auth && auth.isLoggedIn)
  const history = useHistory()
  const [activeTab, setActiveTab] = useState('tracking')
  const tabRef = useRef()

  useAutoIframeHeight(tabRef)

  const handleGoToShipNow = (e) => {
    e.preventDefault()
    if (isIframed) {
      window.parent.postMessage({ redirect: '/ship-now' }, MARKETINGSITE)
    } else {
      if (authenticated) {
        history.push('/ship-with-account')
      } else {
        window.location.href = `${MARKETINGSITE}/ship-now`
      }
    }
  }

  const renderMenuContent = ({
    icon: IconTab,
    iconHover: IconActive,
    ...tab
  }) => (
    <MenuContent className="d-flex justify-content-between flex-column">
      <h3 className="text-body-bigger2 text-white mb-3">{tab.text}</h3>
      <Icon className={tab.id}>
        {activeTab === tab.id ? (
          <IconActive className="icon" />
        ) : (
          <IconTab className="icon" />
        )}
      </Icon>
    </MenuContent>
  )

  const TrackingParent = () => {
    const tracking = useSelector(({ shipping }) => shipping.searchTracking)
    const [view, setView] = useState(trackingViews.trackingForm)
    const [trackingNum, setTrackingNum] = useState('')
    return tracking === trackingViews.trackingForm ? (
      <TrackingForm
        trackingNum={trackingNum}
        setTrackingNum={setTrackingNum}
        setView={setView}
        view={view}
      />
    ) : (
      <TrackingResults trackingNum={trackingNum} setActiveView={setView} />
    )
  }

  return (
    <TabContainer
      id="quick-access-block"
      activeKey={activeTab}
      onSelect={(k) => setActiveTab(k)}
    >
      <QuickAccess className="bg-dark" ref={tabRef} activeTab={activeTab}>
        <Nav variant="tabs" className="justify-content-around">
          {HPQuickAccessTabs.map((tab) => (
            <Nav.Item
              key={tab.id}
              className={joinClasses([
                activeTab === tab.id ? 'active-tab' : '',
                'my-8px mx-0',
              ])}
            >
              {tab.id === 'ship-now' ? (
                <Nav.Link
                  // as={NavLink}
                  // to="/ship-now"
                  title={tab.hoverText}
                  onClick={handleGoToShipNow}
                  className="d-flex flex-column align-items-center justify-content-center"
                >
                  {renderMenuContent(tab)}
                </Nav.Link>
              ) : (
                <Nav.Link
                  eventKey={tab.id !== 'ship-now' ? tab.id : null}
                  className="d-flex flex-column align-items-center justify-content-center"
                  title={tab.hoverText}
                >
                  {renderMenuContent(tab)}
                </Nav.Link>
              )}
            </Nav.Item>
          ))}
        </Nav>
        <TabContent>
          <Suspense fallback={<div className="inner-loading" />}>
            <TabPane eventKey="calculate-rates">
              <CalculateRatesForm />
            </TabPane>
            <TabPane eventKey="pricing">
              <Pricing />
            </TabPane>
            <TabPane eventKey="tracking">{<TrackingParent />}</TabPane>
          </Suspense>
        </TabContent>
      </QuickAccess>
    </TabContainer>
  )
}

export default HomeQuickAccess
